import { useState } from "react";
import { fileUrl } from "@/lib/api";
import FaceMatchBadge from "@/components/FaceMatchBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Info } from "@phosphor-icons/react";

/**
 * Tap-to-open dialog that shows the clock-in selfie and the staff's profile
 * photo side-by-side, plus the auto face-match verdict. Perceptual-hash only
 * — NOT a biometric match; disclaimer surfaced inside the dialog.
 */
export default function SelfiePreviewDialog({ booking }) {
  const [open, setOpen] = useState(false);
  if (!booking?.face_match_verdict && !booking?.clock_in_photo) return null;

  const selfie = booking.clock_in_photo ? fileUrl(booking.clock_in_photo) : null;
  const profile = booking.staff?.profile_photo ? fileUrl(booking.staff.profile_photo) : null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`selfie-preview-open-${booking.id}`}
        className="cursor-pointer hover:brightness-95 transition"
        title="Tap to compare selfie and profile photo"
      >
        <FaceMatchBadge verdict={booking.face_match_verdict} distance={booking.face_match_distance} />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid={`selfie-preview-dialog-${booking.id}`}>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              Face match · {booking.face_match_verdict?.toUpperCase() || "REVIEW"}
            </DialogTitle>
            <DialogDescription>
              {booking.staff?.first_name} {booking.staff?.last_name} — {booking.job_date}
              {booking.face_match_distance != null && (
                <span className="ml-2 font-mono text-xs">
                  · distance = {booking.face_match_distance}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <PhotoCard label="Profile photo" src={profile} testid={`selfie-preview-profile-${booking.id}`} empty="Staff hasn't uploaded a profile photo yet." />
            <PhotoCard label="Clock-in selfie" src={selfie} testid={`selfie-preview-selfie-${booking.id}`} empty="No selfie was uploaded for this shift." />
          </div>

          <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 flex items-start gap-2">
            <Info size={14} className="text-slate-500 shrink-0 mt-0.5" />
            <div>
              Auto face-match uses a perceptual image hash — <b>not a biometric match</b>.
              A red verdict just means the two photos look different in shape and lighting.
              Always eyeball both photos before marking someone as verified or refusing pay.
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PhotoCard({ label, src, empty, testid }) {
  return (
    <div className="rounded-md border border-slate-200 overflow-hidden bg-slate-50">
      <div className="px-3 py-2 text-xs font-semibold text-slate-600 uppercase tracking-wider bg-white border-b border-slate-100">
        {label}
      </div>
      <div className="aspect-square flex items-center justify-center">
        {src ? (
          <img src={src} alt={label} className="w-full h-full object-cover" data-testid={testid} />
        ) : (
          <div className="text-slate-400 text-sm text-center px-4">{empty}</div>
        )}
      </div>
    </div>
  );
}
